import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import formatPrice from "@/core/helpers/formatPrice";
import { imgPlayer } from "@/shared/assets/img";
import { Icon, Tooltip } from "@/shared/ui";
import { InfoState } from "@/domains/business/slice";
import getBusinessImage from "@/domains/business/helpers/getBusinessImage";

type Props = {
  info: InfoState;
  className?: string;
};
export default function BusinessMainInfo({ info, className }: Props) {
  const { t } = useTranslation("business");
  const tooltipContent = (
    <div className="absolute left-7 -top-5.5 w-[32rem] px-4 py-2.5 bg-[#1E1E1E] border border-white/6 rounded-2xl">
      {t("business.popularityDescription")}
    </div>
  );

  return (
    <div className={cn("flex flex-col gap-5", className)}>
      <div className="flex items-center px-4 py-3 bg-dark-800/20 border border-dashed border-[#1A1A1A] rounded-xl">
        <Tooltip content={tooltipContent}>
          <Icon
            category="business"
            name="info"
            className="bg-white/50 size-4.5 transition-all duration-200 hover:bg-white"
          />
        </Tooltip>
        <p className="ml-2.5 text-white/70 font-medium">{t("business.businessPopularity")}</p>
        <div className="flex ml-auto">
          {Array.from([0, 1, 2, 3, 4]).map((item) => (
            <Icon
              key={item}
              category="business"
              name="star"
              className={clsx(
                "m-0.5 size-4.5",
                info?.popularity && item < info.popularity ? "bg-white" : "bg-white/10",
              )}
            />
          ))}
        </div>
      </div>
      <div className="flex flex-col w-full gap-3.5 px-1">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2.5 mb-3.5">
              <img src={imgPlayer} className="size-5.5 rounded" alt="avatar" />
              <div className="text-16 text-white font-bold">{info?.owner?.name}</div>
            </div>
            <div className="flex items-center gap-2.5">
              <Icon category="business" name="location" className="size-5 ml-0.5 bg-white" />
              <div className="text-16 text-white font-medium">{`${info?.address?.streetName} ${info?.address?.index}`}</div>
            </div>
          </div>
          <div
            className="w-42 h-13.5 flex items-center justify-end overflow-hidden"
            style={{
              backgroundImage: `url(${info?.id ? getBusinessImage(info?.id, "logo") : ""})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
            }}
          />
        </div>
      </div>
      <div className="flex flex-col">
        <div className="flex items-center justify-between py-3.5 px-1 border-t border-t-[#191919]">
          <div className="text-16 font-medium text-white/70"> {t("business.cost")}:</div>
          <div className="text-15 font-bold text-white">{formatPrice(info?.price)} ₽</div>
        </div>

        <div className="flex items-center justify-between py-3.5 px-1 border-t border-t-[#191919]">
          <div className="text-16 font-medium text-white/70"> {t("business.feePerDay")}:</div>
          <div className="text-15 font-bold text-white">{formatPrice(info?.baseFee)} ₽</div>
        </div>

        <div className="flex items-center justify-between py-3.5 px-1 border-t border-t-[#191919]">
          <div className="text-16 font-medium text-white/70"> {t("business.employeeFee")}:</div>
          <div className="text-15 font-bold text-white">{formatPrice(info?.employeesFee)} ₽</div>
        </div>
      </div>
    </div>
  );
}
